import moment from "moment";
import { ClosingRecurrence, ClosingTimePolicyModel } from "src/api/product/product-types";
import { UnionDayOfWeekType } from "src/pages/workOrder/workOrder-types";
import { YmdFormat } from "src/utils";

/**
 * 휴무일 UI 상태 ↔ rv API(closing-time-policy) 변환 유틸.
 *
 * UI 는 기존 et 시절 포맷(Date, "MON", "HH:mm")을 그대로 쓰고 API 경계에서만 변환한다.
 * - 요일: "MON"~"SUN" ↔ ISO-8601 1(월)~7(일)
 * - 시간: "HH:mm"(24:00 포함) ↔ 자정 기준 분(0~1440)
 * - 날짜: Date ↔ "YYYY-MM-DD"
 */

// 휴무 사유 최대 글자 수. 초과하면 인풋 하단에 인라인 에러를 노출한다.
export const CLOSING_TITLE_MAX_LENGTH = 30;
export const CLOSING_TITLE_PLACEHOLDER = `노출할 휴무 사유를 ${CLOSING_TITLE_MAX_LENGTH}자 이내로 입력해 주세요.`;
export const CLOSING_TITLE_INVALID_MESSAGE = `${CLOSING_TITLE_MAX_LENGTH}자 이상 입력 불가능 합니다.`;

// 월요일 시작. 인덱스 + 1 이 ISO-8601 요일 번호가 된다.
export const CLOSED_DAY_ORDER = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"] as const;

export type ClosedDay = (typeof CLOSED_DAY_ORDER)[number];

/** 휴무 사유가 글자 수 제한을 넘었는지. 30자까지는 허용한다. */
export const isClosingTitleInvalid = (title?: string) => (title?.length ?? 0) > CLOSING_TITLE_MAX_LENGTH;

/**
 * 등록 폼이 만들어 낼 휴무 항목 수.
 * 한 줄에 휴무 시간을 N개 넣으면 N건이 되므로 줄 수가 아니라 시간 개수를 센다.
 */
export const countClosingRecurrences = (list: { times?: unknown[] }[]): number =>
  list.reduce((count, row) => count + (row.times?.length ?? 0), 0);

/** ["MON", "WED"] → [1, 3] (월~일 순으로 정렬) */
export const toIsoDaysOfWeek = (days?: readonly string[]): number[] =>
  (days ?? [])
    .map((day) => CLOSED_DAY_ORDER.indexOf(day as ClosedDay) + 1)
    .filter((isoDay) => isoDay > 0)
    .sort((a, b) => a - b);

/** [3, 1] → ["MON", "WED"] (월~일 순으로 정렬) */
export const fromIsoDaysOfWeek = (daysOfWeek?: number[]): ClosedDay[] =>
  (daysOfWeek ?? [])
    .filter((isoDay) => isoDay >= 1 && isoDay <= CLOSED_DAY_ORDER.length)
    .sort((a, b) => a - b)
    .map((isoDay) => CLOSED_DAY_ORDER[isoDay - 1]);

/** "09:30" → 570, "24:00" → 1440 */
export const toUnavailableMinutes = (time?: string): number | undefined => {
  if (!time) return undefined;

  const [hour, minute] = time.split(":").map(Number);
  // "09" 처럼 분이 빠지면 minute 가 undefined 가 되는데 Number.isNaN(undefined) 는 false 라 통과한다
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return undefined;

  return hour * 60 + minute;
};

/** 570 → "09:30", 1440 → "24:00" */
export const fromUnavailableMinutes = (minutes?: number): string => {
  if (minutes === undefined || minutes === null) return "";

  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;

  return `${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

/** Date → "2026-09-21" (KST 날짜) */
export const toEffectiveDate = (date?: Date | null): string | undefined => (date ? moment(date).format(YmdFormat.YYYY_MM_DD) : undefined);

/** "2026-09-21" → Date */
export const fromEffectiveDate = (date?: string): Date | null => (date ? moment(date, YmdFormat.YYYY_MM_DD).toDate() : null);

/** 기존 et 목록 컬럼이 쓰던 요일 문자열("월,수") 표기 */
export const DayOfWeekTypeLabel = {
  MON: "월",
  TUE: "화",
  WED: "수",
  THU: "목",
  FRI: "금",
  SAT: "토",
  SUN: "일",
} as const;

export const formatDaysOfWeekLabel = (daysOfWeek?: number[]): string =>
  fromIsoDaysOfWeek(daysOfWeek)
    .map((day) => DayOfWeekTypeLabel[day])
    .join(", ");

/** 목록 한 행. 어느 정책에서 왔는지(개별/기본)를 함께 들고 다닌다. */
export interface ClosingRecurrenceRow extends ClosingRecurrence {
  exclusive?: boolean; // 소속 정책의 적용 방식. true=개별 설정
  facilityId?: string; // 개별 공간 정책이면 공용공간 ID
  facilityType?: string; // 타입 기본 정책이면 공용공간 타입
}

/**
 * 휴무일 정책인지. 조회 시 policyType 으로 걸러 받지만,
 * 다른 정책 타입이 섞여 오면 같은 공간의 엉뚱한 정책을 집어 "기본 설정"으로 잘못 보이므로 한 번 더 확인한다.
 */
const isClosingTimePolicy = (policy: ClosingTimePolicyModel): boolean =>
  policy.key?.policyType === "closing-time-policy" || !!policy.details?.closingTimePolicy;

/**
 * 공용공간 타입 전체에 적용되는 기본 정책. scope 에 facilityId 가 없고 facilityType 만 있다.
 */
export const findDefaultClosingPolicy = (policies?: ClosingTimePolicyModel[], facilityType?: string): ClosingTimePolicyModel | undefined =>
  (policies ?? []).find(
    (policy) => isClosingTimePolicy(policy) && !policy.key?.scope?.facilityId && policy.key?.scope?.facilityType === facilityType,
  );

/**
 * 개별 공간에 지정된 정책. 없으면 그 공간은 기본 정책을 따른다.
 */
export const findFacilityClosingPolicy = (policies?: ClosingTimePolicyModel[], facilityId?: string | number): ClosingTimePolicyModel | undefined =>
  facilityId === undefined || facilityId === null
    ? undefined
    : (policies ?? []).find((policy) => isClosingTimePolicy(policy) && String(policy.key?.scope?.facilityId ?? "") === String(facilityId));

/**
 * 개별 설정을 쓰고 있는지. rv 의 exclusive 는 et 의 isDefault 와 의미가 반대다.
 * 정책이 아직 없으면 기본 설정으로 본다.
 */
export const isExclusiveClosingPolicy = (policy?: ClosingTimePolicyModel): boolean => policy?.details?.closingTimePolicy?.exclusive === true;

/** 정책 1건을 휴무 항목 단위로 펼친다. */
export const flattenClosingPolicy = (policy?: ClosingTimePolicyModel): ClosingRecurrenceRow[] =>
  (policy?.details?.closingTimePolicy?.recurrences ?? []).map((recurrence) => ({
    ...recurrence,
    exclusive: policy?.details?.closingTimePolicy?.exclusive,
    facilityId: policy?.key?.scope?.facilityId,
    facilityType: policy?.key?.scope?.facilityType,
  }));

/**
 * "2026-04-15" → "2026-04-15(수)"
 * 요일은 moment locale 설정에 기대지 않고 ISO 요일로 직접 붙인다(휴무 요일 컬럼과 같은 라벨).
 */
export const formatClosingDate = (date?: string): string => {
  if (!date) return "";

  const parsed = moment(date, YmdFormat.YYYY_MM_DD);
  if (!parsed.isValid()) return date;

  return `${parsed.format(YmdFormat.YYYY_MM_DD)}(${DayOfWeekTypeLabel[CLOSED_DAY_ORDER[parsed.isoWeekday() - 1]]})`;
};

/** 요약에서 쓰는 휴무 기간 한 줄 표기 (요일 없음) */
export const formatClosingPeriodLabel = (recurrence: ClosingRecurrence): string => {
  const from = recurrence.effectiveFrom ?? "";
  const to = recurrence.effectiveTo ?? "";

  return from === to ? from : `${from} ~ ${to}`;
};

/** 요약에서 쓰는 휴무 기간 한 줄 표기 (요일 포함) */
export const formatClosingPeriodLabelWithWeek = (recurrence: ClosingRecurrence): string => {
  const from = formatClosingDate(recurrence.effectiveFrom);
  const to = formatClosingDate(recurrence.effectiveTo);

  return from === to ? from : `${from} ~ ${to}`;
};

/** 목록/요약에서 쓰는 휴무 시간 표기 */
export const formatClosingTimeLabel = (recurrence: ClosingRecurrence): string =>
  `${fromUnavailableMinutes(recurrence.unavailableFrom)} ~ ${fromUnavailableMinutes(recurrence.unavailableUntil)}`;

/** UI 상태 한 줄(기간/요일) + 시간 한 쌍 → API 휴무 항목 */
export const toClosingRecurrence = (closedPeriod: {
  startDate?: Date | null;
  endDate?: Date | null;
  days?: readonly UnionDayOfWeekType[] | readonly string[];
  title?: string;
  startTime?: string;
  endTime?: string;
}): ClosingRecurrence => ({
  title: closedPeriod.title || undefined,
  effectiveFrom: toEffectiveDate(closedPeriod.startDate),
  effectiveTo: toEffectiveDate(closedPeriod.endDate),
  daysOfWeek: toIsoDaysOfWeek(closedPeriod.days),
  unavailableFrom: toUnavailableMinutes(closedPeriod.startTime),
  unavailableUntil: toUnavailableMinutes(closedPeriod.endTime),
});
